import React from 'react';
import { useTranslations } from '../../hooks/useTranslations';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Typography,
  useTheme,
  IconButton
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LanguageIcon from '@mui/icons-material/Language';

const languages = [
  { code: 'en', name: 'English', flag: 'us.svg' },
  { code: 'fr', name: 'Français', flag: 'fr.svg' },
  { code: 'es', name: 'Español', flag: 'es.svg' },
  { code: 'ru', name: 'Русский', flag: 'ru.svg' }
];

const LanguageSelector = ({ textColor, isMobile = false, iconOnly = false }) => {
  const { i18n, changeLanguage, isLanguageReady } = useTranslations();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  // Attendre que i18n soit initialisé
  if (!i18n.isInitialized) {
    return null; // Ou un spinner
  }

  // Normaliser la langue (fr-FR -> fr)
  const normalizedLanguage = i18n.language?.split('-')[0] || 'fr';
  
  const currentLanguage = languages.find(lang => lang.code === normalizedLanguage) || languages[1]; // Français par défaut

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = async (languageCode) => {
    await changeLanguage(languageCode);
    handleClose();
  };

  return (
    <Box>
      {iconOnly ? (
        <IconButton
        aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
          sx={{
            color: textColor || theme.palette.primary.main,
            width: 40,
            height: 40,
            padding: 0
          }}
        >
          <Box
            component="img"
            src={`${process.env.PUBLIC_URL}/svg/flags/${currentLanguage.flag}`}
            alt={`${currentLanguage.name} flag`}
            sx={{
              width: 28,
              height: 21,
              objectFit: 'cover',
              borderRadius: '4px'
            }}
          />
        </IconButton>
      ) : (
        <Button
          onClick={handleClick}
          aria-expanded={open ? 'true' : false}
          aria-controls={open ? 'language-menu' : undefined}
          variant="secondary"
          endIcon={<KeyboardArrowDownIcon sx={{ color: theme.palette.primary.main }} />}
          sx={{
            color: textColor || theme.palette.primary.main,
            textTransform: 'none',
            border: 0,
            fontWeight: 500,
            '&:hover': {
              backgroundColor: theme.palette.background.default,
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              component="img"
              src={`${process.env.PUBLIC_URL}/svg/flags/${currentLanguage.flag}`}
              alt={""}
              sx={{
                width: 20,
                height: 15,
                objectFit: 'cover',
                borderRadius: '2px'
              }}
            />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {currentLanguage.name}
            </Typography>
            {!isLanguageReady && (
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: 'orange',
                  animation: 'pulse 1s infinite'
                }}
              />
            )}
          </Box>
        </Button>
      )}

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 180,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            border: '1px solid rgba(0,0,0,0.05)',
          }
        }}
      >
        {languages.map((language) => (
          <MenuItem 
            key={language.code}
            onClick={() => handleLanguageChange(language.code)}
            selected={language.code === normalizedLanguage}
            sx={{
              py: 1.5,
              px: 2,
              '&.Mui-selected': {
                backgroundColor: 'rgba(12, 48, 88, 0.08)',
                '&:hover': {
                  backgroundColor: 'rgba(12, 48, 88, 0.12)',
                }
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
              <Box
                component="img"
                src={`${process.env.PUBLIC_URL}/svg/flags/${language.flag}`}
                alt={`${language.name} flag`}
                sx={{
                  width: 20,
                  height: 15,
                  objectFit: 'cover',
                  borderRadius: '2px'
                }}
              />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {language.name}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default LanguageSelector; 